/**
 * RoleSwitcher
 * A "Switch Role" entry for the header user-menu. Lets a signed-in multi-role user
 * change their active role AFTER login (the login-time picker is RoleSelectionGate).
 *
 * - Renders nothing when the user has ≤ 1 role (nothing to switch).
 * - Opens a dialog listing the user's roles (role + unit); the current one is marked.
 * - On pick → POST /users/me/select-role → persist the scoped token → reload so
 *   permissions/menu re-resolve for the new role/unit.
 *
 * Degrades gracefully: if the backend doesn't expose /me/roles yet, it hides itself.
 */
import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { profileService, type MyRole } from '../../services/profile.service';

function decodeClaim(token: string | null, key: string): string | undefined {
  if (!token) return undefined;
  try {
    const seg = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    return seg ? JSON.parse(atob(seg))?.[key] : undefined;
  } catch {
    return undefined;
  }
}

const RoleSwitcher = () => {
  const [roles, setRoles] = useState<MyRole[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const currentRbid =
    (typeof window !== 'undefined' && localStorage.getItem('activeRoleBindingId')) ||
    decodeClaim(token, 'roleBindingId');

  useEffect(() => {
    let mounted = true;
    profileService
      .getMyRoles()
      .then((r) => mounted && setRoles(r))
      .catch(() => mounted && setRoles([]));
    return () => {
      mounted = false;
    };
  }, []);

  if (roles.length <= 1) return null; // nothing to switch

  const current = roles.find((r) => r.roleBindingId === currentRbid);

  const pick = async (roleBindingId: string) => {
    if (busy) return;
    if (roleBindingId === currentRbid) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const res = await profileService.selectRole(roleBindingId);
      if (res?.accessToken) {
        localStorage.setItem('accessToken', res.accessToken);
        localStorage.setItem('activeRoleBindingId', res.roleBindingId);
        window.location.reload();
      } else {
        setBusy(false);
      }
    } catch {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="Header.SwitchRole"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <i className="pi pi-users text-gray-500" />
        <span className="flex-1 text-left">Switch Role</span>
        {current?.roleName && (
          <span className="text-xs text-gray-400 truncate max-w-[7rem]">{current.roleName}</span>
        )}
      </button>

      <Dialog
        header="Switch role"
        visible={open}
        onHide={() => !busy && setOpen(false)}
        style={{ width: '360px' }}
        dismissableMask
      >
        <div className="flex flex-col gap-2">
          {roles.map((r) => {
            const isCurrent = r.roleBindingId === currentRbid;
            return (
              <button
                key={r.roleBindingId}
                type="button"
                onClick={() => pick(r.roleBindingId)}
                disabled={busy}
                className={`flex items-center justify-between text-left px-3 py-2.5 rounded-xl border transition-colors disabled:opacity-50 ${
                  isCurrent
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-line hover:border-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-900/10'
                }`}
              >
                <span className="flex flex-col">
                  <span className="font-medium text-ink">
                    {r.roleName || r.roleKey || 'Role'}
                  </span>
                  <span className="text-xs text-ink-muted">
                    <i className="pi pi-building mr-1" style={{ fontSize: '0.7rem' }} />
                    {r.isGlobal ? 'All units (global)' : r.unitName || 'Unit'}
                  </span>
                </span>
                {isCurrent && <span className="text-xs font-medium text-blue-600">Current</span>}
              </button>
            );
          })}
        </div>
      </Dialog>
    </>
  );
};

export default RoleSwitcher;
